import { forwardRef } from "react";
import { tv } from "tailwind-variants";

import type { AnchorHTMLAttributes, CSSProperties, MouseEventHandler, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";

export const linkVariants = tv({
  base: "inline cursor-pointer text-blue-500 transition-colors hover:text-blue-600",
  variants: {
    disabled: {
      true: "pointer-events-none cursor-not-allowed text-secondary opacity-50 hover:text-secondary",
      false: "",
    },
    underline: {
      true: "underline hover:underline",
      hover: "hover:underline",
      none: "no-underline",
    },
  },
  defaultVariants: {
    disabled: false,
    underline: "hover",
  },
});

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">,
    VariantProps<typeof linkVariants> {
  /** URL to navigate to (renders as anchor) */
  href?: string;
  /** Click handler (used when no href, renders as button-like span) */
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLSpanElement>;
  /** Disables the link */
  disabled?: boolean;
  /** Inline styles */
  style?: CSSProperties;
  /** Link content */
  children?: ReactNode;
}

/**
 * Link component for navigation and clickable text.
 *
 * When `href` is provided, renders as an anchor tag.
 * When only `onClick` is provided, renders as a span with button semantics.
 *
 * @example
 * // Navigation link
 * <Link href="/dashboard">Go to Dashboard</Link>
 *
 * @example
 * // Action link
 * <Link onClick={() => doSomething()}>Click me</Link>
 *
 * @example
 * // Disabled link
 * <Link disabled onClick={handleClick}>Disabled</Link>
 */
export const Link = forwardRef<HTMLAnchorElement | HTMLSpanElement, LinkProps>(
  ({ className, disabled = false, underline, href, onClick, style, children, ...props }, ref) => {
    const classes = linkVariants({ className, disabled, underline });

    // If href is provided, render as anchor
    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={disabled ? undefined : href}
          className={classes}
          style={style}
          onClick={disabled ? undefined : onClick}
          aria-disabled={disabled || undefined}
          {...props}
        >
          {children}
        </a>
      );
    }

    // Otherwise render as span with button-like behavior
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={classes}
        style={style}
        onClick={disabled ? undefined : onClick}
        onKeyDown={
          disabled
            ? undefined
            : e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>);
                }
              }
        }
        aria-disabled={disabled || undefined}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Link.displayName = "Link";
